import type { PortalApplication } from '../../../model/portalModels';
import { resolveTrustedAppUrl } from '../../../runtime/wujieLoader';
import type { OperationInitializationFailure } from '@jukun/platform-sdk/auth';

export type OperationFailureStage =
  'detail' | 'availability' | 'route'
  | 'probe' | 'mount' | 'render';

export type OperationProbeResult =
  | { ok: true; method: 'HEAD' | 'GET' }
  | {
    ok: false;
    code: 'TIMEOUT' | 'NETWORK' | 'HTTP_STATUS';
    status?: number;
  };

export function validateOperationPageRoute(pageRoute: string) {
  if (!pageRoute.startsWith('/')
      || pageRoute.startsWith('//')
      || pageRoute.includes('\\')
      || /[?#*{}]/.test(pageRoute)
      || pageRoute.split('/').includes('..')
      || [...pageRoute].some(character =>
        character.charCodeAt(0) < 32)) {
    throw new Error('操作页面路由不合法');
  }
  return pageRoute;
}

export function resolveOperationTargetUrl(
  application: PortalApplication,
  pageRoute: string,
  currentHref = window.location.href,
) {
  const route = validateOperationPageRoute(pageRoute);
  return resolveTrustedAppUrl(
    application,
    `${application.entry.basePath}${route}`,
    currentHref,
  );
}

export async function probeOperationRoute(
  url: string,
  options: {
    timeoutMs?: number;
    fetcher?: typeof fetch;
    signal?: AbortSignal;
  } = {},
): Promise<OperationProbeResult> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (options.signal?.aborted) abort();
  else options.signal?.addEventListener('abort', abort, { once: true });
  const timeout = globalThis.setTimeout(
    () => controller.abort(),
    options.timeoutMs ?? 4_000,
  );
  const fetcher = options.fetcher ?? fetch;
  const request = async (method: 'HEAD' | 'GET') => {
    const response = await fetcher(url, {
      method,
      credentials: 'omit',
      cache: 'no-store',
      headers: method === 'GET'
        ? { Range: 'bytes=0-0' } : undefined,
      signal: controller.signal,
    });
    if (method === 'GET' && response.body) {
      void response.body.cancel().catch(() => undefined);
    }
    return response;
  };
  try {
    let response = await request('HEAD');
    if (response.status === 405 || response.status === 501) {
      response = await request('GET');
      return response.ok
        ? { ok: true, method: 'GET' }
        : {
          ok: false,
          code: 'HTTP_STATUS',
          status: response.status,
        };
    }
    return response.ok
      ? { ok: true, method: 'HEAD' }
      : {
        ok: false,
        code: 'HTTP_STATUS',
        status: response.status,
      };
  } catch (error) {
    if (controller.signal.aborted) {
      return { ok: false, code: 'TIMEOUT' };
    }
    return { ok: false, code: 'NETWORK' };
  } finally {
    globalThis.clearTimeout(timeout);
    options.signal?.removeEventListener('abort', abort);
  }
}

export function waitForOperationRoute(
  subscribe: (
    handlers: Readonly<{
      ready: (pageRoute: string) => void;
      failed: (failure: OperationInitializationFailure) => void;
    }>,
  ) => void,
  expectedRoute: string,
  timeoutMs = 8_000,
) {
  return new Promise<void>((resolve, reject) => {
    let settled = false;
    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout(timeout);
      callback();
    };
    const timeout = globalThis.setTimeout(
      () => finish(() => reject(new Error('操作页面未报告路由就绪'))),
      timeoutMs,
    );
    subscribe({
      ready: pageRoute => {
        if (pageRoute !== expectedRoute) return;
        finish(resolve);
      },
      failed: failure => {
        if (failure.pageRoute !== expectedRoute) return;
        finish(() => reject(new Error(
          `${failure.message}（${failure.code}）`,
        )));
      },
    });
  });
}
